/**
 * The accounts of the installation, for root and those it gave `accounts.manage`.
 *
 * Every change carries `confirmed`: the token the server gave a moment ago for the
 * password (and the code of the second factor) typed again (POST /auth/confirm, see
 * features/access-control/confirmation.tsx). It goes in a header of its own.
 */
import { baseApi } from '../baseApi';
import type {
  AccountIn,
  AccountOut,
  AccountPageOut,
  AssignableRole,
  AuditPageOut,
  Permission,
  Role,
} from '../types';

/** A change, and the confirmation that it is the one asking. */
type Confirmed<T> = T & { confirmed: string };

const confirmedBy = (token: string) => ({ 'X-Confirm-Token': token });

export const accessApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    listAccounts: build.query<
      AccountPageOut,
      { q?: string | undefined; role?: Role | undefined; cursor?: string | undefined }
    >({
      query: (params) => ({ url: '/access/users', params }),
      providesTags: ['Accounts'],
    }),
    listAudit: build.query<AuditPageOut, { before?: string | undefined }>({
      query: (params) => ({ url: '/access/audit', params }),
      providesTags: ['Audit'],
    }),
    createAccount: build.mutation<AccountOut, Confirmed<AccountIn>>({
      query: ({ confirmed, ...body }) => ({
        url: '/access/users',
        method: 'POST',
        body,
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
    }),
    setAccountRole: build.mutation<AccountOut, Confirmed<{ id: string; role: AssignableRole }>>({
      query: ({ id, role, confirmed }) => ({
        url: `/access/users/${id}/role`,
        method: 'PUT',
        body: { role },
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
    }),
    // Shows the change at once in every list on screen and takes it back if the
    // server refuses; the lists are then read again, which settles filters and paging.
    setAccountPermissions: build.mutation<
      AccountOut,
      Confirmed<{ id: string; permissions: Permission[] }>
    >({
      query: ({ id, permissions, confirmed }) => ({
        url: `/access/users/${id}/permissions`,
        method: 'PUT',
        body: { permissions },
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
      async onQueryStarted({ id, permissions }, { dispatch, getState, queryFulfilled }) {
        const patches = accessApi.util
          .selectInvalidatedBy(getState(), ['Accounts'])
          .filter((entry) => entry.endpointName === 'listAccounts')
          .map((entry) =>
            dispatch(
              accessApi.util.updateQueryData('listAccounts', entry.originalArgs, (page) => {
                const account = page.items.find((item) => item.id === id);
                if (account) account.permissions = permissions;
              }),
            ),
          );
        try {
          await queryFulfilled;
        } catch {
          for (const patch of patches) patch.undo();
        }
      },
    }),
    setAccountDisabled: build.mutation<AccountOut, Confirmed<{ id: string; disabled: boolean }>>({
      query: ({ id, disabled, confirmed }) => ({
        url: `/access/users/${id}/${disabled ? 'disable' : 'enable'}`,
        method: 'POST',
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
    }),
    forceNewPassword: build.mutation<AccountOut, Confirmed<{ id: string }>>({
      query: ({ id, confirmed }) => ({
        url: `/access/users/${id}/password-reset`,
        method: 'POST',
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
    }),
    deleteAccount: build.mutation<void, Confirmed<{ id: string }>>({
      query: ({ id, confirmed }) => ({
        url: `/access/users/${id}`,
        method: 'DELETE',
        headers: confirmedBy(confirmed),
      }),
      invalidatesTags: ['Accounts', 'Audit'],
    }),
  }),
});

export const {
  useListAccountsQuery,
  useListAuditQuery,
  useCreateAccountMutation,
  useSetAccountRoleMutation,
  useSetAccountPermissionsMutation,
  useSetAccountDisabledMutation,
  useForceNewPasswordMutation,
  useDeleteAccountMutation,
} = accessApi;
